// The preferences the shell owns, and nothing else.
//
// All of them are about *this browser*: whether the navigation is showing, and
// what each of the two favourite surfaces holds. None is worth a row in the
// database, none should travel to another user on the same workspace, and none
// justifies a global store — so each is `useState` plus one key in
// `localStorage`, read defensively because Safari in private mode throws on
// access rather than returning null.

import { useCallback, useEffect, useMemo, useState } from "react";
import { NAV_FOOTER, visibleGroups, type NavContext } from "./nav";
import type { NavIcon } from "./icons";
import { PLATFORM_LABEL, type Connection } from "../lib/types";

const SIDEBAR_KEY = "simino.sidebar";
const FAVOURITES_KEY = "simino.favourites";

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // A preference that cannot be saved is still a preference for this tab.
  }
}

/**
 * Whether the main sidebar is expanded, remembered across navigations and
 * reloads.
 *
 * The initial value is read synchronously rather than in an effect: a sidebar
 * that paints expanded and then snaps shut on every page load is worse than one
 * that never remembered anything. `entry` guards the pre-render, which has no
 * `window` (see `entry-prerender.tsx`).
 */
export function useSidebar(): {
  expanded: boolean;
  toggle: () => void;
  setExpanded: (on: boolean) => void;
} {
  const [expanded, setExpandedState] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return read(SIDEBAR_KEY) !== "collapsed";
  });

  const setExpanded = useCallback((on: boolean) => {
    setExpandedState(on);
    write(SIDEBAR_KEY, on ? "expanded" : "collapsed");
  }, []);

  const toggle = useCallback(() => setExpanded(!expanded), [expanded, setExpanded]);

  return { expanded, toggle, setExpanded };
}

/**
 * One thing that can be kept within reach: a screen, or a connected account.
 *
 * Favourites used to be accounts only, because the rail was built for account
 * switching. That was too narrow — for most of the people using this the screen
 * they open twenty times a day is Calendar or Posts, and there was no way to
 * say so. A favourite is now anything on the navigation as well.
 */
export interface FavouriteItem {
  /** `nav:<id>` or `acct:<uuid>`. Prefixed, so a screen and an account can
   *  never collide, and so a stored key says what it refers to. */
  key: string;
  kind: "nav" | "account";
  label: string;
  /** The second line in the picker: the group it came from, or its platform. */
  hint: string;
  route: string;
  /** Screens only. */
  icon?: NavIcon;
  /** Screens only — the disclosure `nav.ts` requires every item to carry. */
  title?: string;
  /** Accounts only. */
  connection?: Connection;
}

/**
 * The two places favourites appear, edited separately.
 *
 * They were one list until it became clear that they are not one question. The
 * top bar is wide, labelled and read: it suits screens and a couple of
 * accounts. The rail is a column of 30px icons always on screen: it suits the
 * handful of things reached without looking. Forcing the same six items into
 * both meant every choice was a compromise between two shapes, so each keeps
 * its own list and its own picker.
 */
export type FavouriteSurface = "top" | "rail";

/**
 * Items pinned to a surface whatever the person chooses.
 *
 * Compose is in the rail permanently. It is the thing this product is for, it
 * is one press from every screen, and a rail somebody had accidentally emptied
 * of it would be a rail that had lost the point. The picker shows it ticked and
 * unticks nothing — an offer that cannot be taken is worse than a row that
 * plainly says it is fixed.
 */
const FIXED: Record<FavouriteSurface, string[]> = {
  top: [],
  rail: ["nav:compose"],
};

const navKey = (id: string) => `nav:${id}`;
const acctKey = (id: string) => `acct:${id}`;

/**
 * What this person could choose from, in the order the picker offers it.
 *
 * Built from the same `nav.ts` the sidebar renders, so a screen they may not
 * see is not a screen they can pin — and a screen added to the navigation is
 * offered here the same day, with no second list to remember to update.
 */
export function favouriteCandidates(ctx: NavContext, connections: Connection[]): FavouriteItem[] {
  const screens: FavouriteItem[] = [
    ...visibleGroups(ctx).flatMap((group) =>
      group.items.map((item) => ({
        key: navKey(item.id),
        kind: "nav" as const,
        label: item.label,
        hint: group.label,
        route: item.route,
        icon: item.icon,
        title: item.title,
      }))),
    ...NAV_FOOTER.map((item) => ({
      key: navKey(item.id),
      kind: "nav" as const,
      label: item.label,
      hint: "Account",
      route: item.route,
      icon: item.icon,
      title: item.title,
    })),
  ];

  const accounts: FavouriteItem[] = connections
    .filter((c) => c.status === "active")
    .map((c) => ({
      key: acctKey(c.id),
      kind: "account" as const,
      label: c.display_name,
      hint: PLATFORM_LABEL[c.platform],
      // Pressing an account composes to it. See `FavoritesRail`.
      route: `/compose?to=${encodeURIComponent(c.id)}`,
      connection: c,
    }));

  return [...screens, ...accounts];
}

/** Everything pinnable, computed once and shared by both surfaces and search. */
export function useFavouriteCandidates(
  ctx: NavContext,
  connections: Connection[],
): FavouriteItem[] {
  return useMemo(() => favouriteCandidates(ctx, connections), [ctx, connections]);
}

export interface FavouritesState {
  /** What the surface shows, in order, fixed items first. */
  favourites: FavouriteItem[];
  candidates: FavouriteItem[];
  isFavourite: (key: string) => boolean;
  /** Keys this surface holds regardless — offered ticked and not unticked. */
  fixed: string[];
  toggle: (key: string) => void;
  /** True while the person has chosen; false means "the default". */
  customised: boolean;
  reset: () => void;
}

/**
 * One surface's favourites.
 *
 * The default, until somebody chooses, is every active account — which is what
 * this was before it could hold screens at all. Choosing anything replaces the
 * default outright: once somebody has said what they want, silently adding a
 * newly connected account to their chosen row would be the tool arguing with
 * them.
 *
 * Stored keys that no longer resolve — a disconnected account, a screen a role
 * change took away — simply stop appearing. Nothing is cleaned up, because the
 * membership that removed them can be given back.
 */
export function useFavourites(
  workspaceId: string | null,
  surface: FavouriteSurface,
  candidates: FavouriteItem[],
): FavouritesState {
  const key = workspaceId ? `${FAVOURITES_KEY}.${surface}.${workspaceId}` : null;
  // What the single shared list held before the two surfaces were separated.
  // Read as the seed for both rather than migrated into one of them, so nobody
  // loses a choice they had already made — and written back under the new key
  // the first time either surface is edited.
  const legacyKey = workspaceId ? `${FAVOURITES_KEY}.${workspaceId}` : null;
  const [chosen, setChosen] = useState<string[] | null>(null);

  // Re-read on a workspace switch: the list belongs to the workspace, and
  // carrying one workspace's favourites into another would offer accounts that
  // are not there.
  useEffect(() => {
    if (!key) {
      setChosen(null);
      return;
    }
    const raw = read(key) ?? (legacyKey ? read(legacyKey) : null);
    if (!raw) {
      setChosen(null);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        setChosen(null);
        return;
      }
      setChosen(parsed
        .filter((x): x is string => typeof x === "string")
        // Written before favourites could be screens, when the value was a bare
        // connection id. Read forward rather than migrated: there is nothing to
        // gain by rewriting somebody's preference behind their back.
        .map((x) => (x.includes(":") ? x : acctKey(x))));
    } catch {
      setChosen(null);
    }
  }, [key, legacyKey]);

  const fixed = FIXED[surface];

  const favourites = useMemo(() => {
    const byKey = new Map(candidates.map((c) => [c.key, c]));
    const pinned = fixed.map((k) => byKey.get(k)).filter((c): c is FavouriteItem => !!c);

    const rest = !chosen || chosen.length === 0
      ? candidates.filter((c) => c.kind === "account")
      // The person's order, not ours.
      : chosen.map((k) => byKey.get(k)).filter((c): c is FavouriteItem => !!c);

    // Every choice gone — disconnected, or a role change. Falling back to the
    // default beats an empty surface with no way back into it.
    const chosenRest = chosen && chosen.length > 0 && rest.length === 0
      ? candidates.filter((c) => c.kind === "account")
      : rest;

    return [...pinned, ...chosenRest.filter((c) => !fixed.includes(c.key))];
  }, [chosen, candidates, fixed]);

  const save = useCallback((next: string[]) => {
    setChosen(next);
    if (key) write(key, JSON.stringify(next));
  }, [key]);

  const toggle = useCallback((itemKey: string) => {
    if (fixed.includes(itemKey)) return;
    // The first change has to start from what is on screen, not from nothing:
    // "the accounts" is a default, and removing one of six must leave five.
    const base = chosen && chosen.length > 0
      ? chosen
      : favourites.filter((f) => !fixed.includes(f.key)).map((f) => f.key);
    save(base.includes(itemKey)
      ? base.filter((k) => k !== itemKey)
      : [...base, itemKey]);
  }, [chosen, favourites, fixed, save]);

  const isFavourite = useCallback(
    (itemKey: string) => favourites.some((f) => f.key === itemKey),
    [favourites],
  );

  return {
    favourites,
    candidates,
    isFavourite,
    fixed,
    toggle,
    customised: !!chosen && chosen.length > 0,
    reset: () => save([]),
  };
}
